import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Key, CheckCircle, XCircle, ExternalLink, Eye, EyeOff,
  Plus, Building2, Bot, ChevronRight, Sparkles,
} from 'lucide-react';
import { createChannel, getChannelBotInfo } from '../services/api';
import { useChannel } from '../context/ChannelContext';
import { useWorkspace } from '../context/WorkspaceContext';

export const Channels = () => {
  const { channels, refreshChannels, selectedChannelId, selectChannel } = useChannel();
  const { limits, usage, isAtChannelLimit, openProDialog, refreshWorkspaces } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const openAdd = () => (isAtChannelLimit ? openProDialog() : setDialogOpen(true));
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [description, setDescription] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [botInfo, setBotInfo] = useState({});

  useEffect(() => {
    if (location.state?.openAddChannel) {
      if (isAtChannelLimit) openProDialog(); else setDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBotInfo = useCallback(async (channelId) => {
    if (botInfo[channelId]) return;
    try {
      const info = await getChannelBotInfo(channelId);
      setBotInfo(prev => ({ ...prev, [channelId]: info }));
    } catch {}
  }, [botInfo]);

  useEffect(() => {
    channels.filter(c => c.isValid).forEach(c => fetchBotInfo(c.id));
  }, [channels]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => { setName(''); setToken(''); setDescription(''); setShowToken(false); };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Please enter a channel name'); return; }
    if (!token.trim()) { toast.error('Please enter the channel access token'); return; }
    setSaving(true);
    try {
      const result = await createChannel({ name, channelAccessToken: token, description });
      if (channels.length === 0) selectChannel(result.id);
      await Promise.all([refreshChannels(), refreshWorkspaces()]);
      toast[result.isValid ? 'success' : 'warning'](
        result.isValid ? 'Channel added and validated' : 'Channel added, but token appears invalid'
      );
      setDialogOpen(false);
      resetForm();
    } catch (e) {
      if (e.response?.data?.code === 'channel_limit_reached') {
        setDialogOpen(false);
        resetForm();
        await refreshWorkspaces();
        openProDialog();
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to save channel');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Channels</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Manage your LINE Messaging API channels
            {limits.maxChannels != null ? (
              <> · <span data-testid="channel-usage" className={isAtChannelLimit ? 'text-amber-400' : ''}>
                {usage.channels} of {limits.maxChannels} channels used
              </span></>
            ) : (
              <> · <span data-testid="channel-usage" className="text-[#00E05E]">Pro plan · unlimited channels</span></>
            )}
          </p>
        </div>
        <Button className="btn-primary w-full sm:w-auto" onClick={openAdd} data-testid="add-channel-btn">
          {isAtChannelLimit ? (<><Sparkles className="w-4 h-4 mr-2" />Upgrade to add more</>) : (<><Plus className="w-4 h-4 mr-2" />Add Channel</>)}
        </Button>
      </div>

      {isAtChannelLimit && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4" data-testid="channel-limit-banner">
          <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-200 flex-1">
            You've reached the Free plan limit of {limits.maxChannels} channels. <span className="font-semibold">Upgrade to Pro</span> for unlimited channels.
          </p>
          <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" onClick={openProDialog}>
            See Pro
          </Button>
        </div>
      )}

      {channels.length === 0 ? (
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#27272A] flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-[#52525B]" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No channels configured</h3>
          <p className="text-[#A1A1AA] text-sm mb-6 max-w-md mx-auto">Add your LINE channel to start creating and publishing rich menus</p>
          <Button className="btn-primary" onClick={openAdd}>
            <Plus className="w-4 h-4 mr-2" />Add Your First Channel
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {channels.map(channel => (
            <button
              key={channel.id}
              onClick={() => navigate(`/channels/${channel.id}`)}
              className="w-full bg-[#18181B] border border-[#27272A] rounded-lg p-4 sm:p-5 text-left hover:border-[#3F3F46] transition-colors group"
              data-testid={`channel-card-${channel.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#06C755]/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-[#06C755]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{channel.name}</span>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${channel.isValid ? 'bg-[#06C755]/10 text-[#06C755]' : 'bg-yellow-500/10 text-yellow-500'}`}>
                      {channel.isValid ? <><CheckCircle className="w-3 h-3" />Valid</> : <><XCircle className="w-3 h-3" />Invalid</>}
                    </span>
                    {selectedChannelId === channel.id && (
                      <span className="px-2 py-0.5 rounded bg-[#00E05E]/10 text-[#00E05E] text-xs">Selected</span>
                    )}
                  </div>
                  {botInfo[channel.id] ? (
                    <div className="flex items-center gap-2 mt-0.5">
                      <Bot className="w-3 h-3 text-[#52525B]" />
                      <span className="text-sm text-[#A1A1AA]">{botInfo[channel.id].displayName}</span>
                      {botInfo[channel.id].basicId && <span className="text-xs text-[#52525B] font-mono">{botInfo[channel.id].basicId}</span>}
                    </div>
                  ) : channel.description ? (
                    <p className="text-sm text-[#71717A] mt-0.5 truncate">{channel.description}</p>
                  ) : null}
                </div>
                <ChevronRight className="w-4 h-4 text-[#52525B] group-hover:text-[#A1A1AA] transition-colors flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 p-4 bg-[#18181B] border border-[#27272A] rounded-lg">
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
          <Key className="w-4 h-4 text-[#06C755]" />
          How to get your Channel Access Token
        </h3>
        <ol className="space-y-2 text-sm text-[#A1A1AA]">
          <li className="flex gap-2"><span className="font-mono text-[#00E05E]">1.</span><span>Go to the LINE Developers Console</span></li>
          <li className="flex gap-2"><span className="font-mono text-[#00E05E]">2.</span><span>Select or create a Messaging API channel</span></li>
          <li className="flex gap-2"><span className="font-mono text-[#00E05E]">3.</span><span>Navigate to "Messaging API" tab</span></li>
          <li className="flex gap-2"><span className="font-mono text-[#00E05E]">4.</span><span>Issue or copy your Channel Access Token (long-lived)</span></li>
        </ol>
        <a href="https://developers.line.biz/console/" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 text-sm text-[#06C755] hover:underline">
          Open LINE Developers Console <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Add Channel dialog */}
      <Dialog open={dialogOpen} onOpenChange={open => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Channel</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Add a new LINE channel to manage its rich menus</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-[#A1A1AA]">Channel Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., My Official Account"
                className="bg-[#09090B] border-[#27272A] mt-1" data-testid="channel-name-input" />
            </div>
            <div>
              <Label className="text-sm text-[#A1A1AA]">Channel Access Token</Label>
              <div className="relative mt-1">
                <Input type={showToken ? 'text' : 'password'} value={token} onChange={e => setToken(e.target.value)}
                  placeholder="Enter your token" className="bg-[#09090B] border-[#27272A] pr-10 font-mono text-sm"
                  data-testid="channel-token-input" />
                <button type="button" onClick={() => setShowToken(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]">
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#A1A1AA]">Description (optional)</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)}
                placeholder="e.g., Production channel" className="bg-[#09090B] border-[#27272A] mt-1"
                data-testid="channel-description-input" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="border-[#27272A]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-channel-btn">
              {saving ? 'Saving...' : 'Add Channel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Channels;
